/**
 * Page: EventDetailPage
 * Event detail page with tabs for different sections
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowLeft, CalendarDays, Edit, MoreVertical, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { usePermissions } from '@/hooks/usePermissions';
import { InlineLoader } from '@/components/ui/pulsing-loader';
import { useEvent, useEventSessions, useSessionMutations } from '../hooks';
import { getEventById } from '@/data/eventRepository';
import { getEventPayments } from '@/Services/eventPayments.service';
import { useEventParticipantStatistics } from '../hooks/useEventParticipants';
import {
  EventOverviewTab,
  EventSessionsTab,
  EventParticipantsTab,
  EventPaymentsTab,
  EventReportsTab,
  PricingRulesTab,
  SessionFormDialog,
  EventStatusControls,
} from '../components';
import { useEventStatusTransition } from '../hooks/useEventStatusTransition';
import { acceptsRegistrations, formatDateRange } from '@/models/event';
import type { Event, EventSession } from '@/types/event.types';
import type { EventDetailTab } from '../types';

export function EventDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Permissions (UI gating; DB enforces the same via RLS/RPCs)
  const { can, canAny } = usePermissions();
  const canEdit = can('event.edit');
  const canTransition = canAny('event.transition_status', 'event.edit');
  const canDelete = can('event.delete');

  // Event (React Query: refreshes after edits / status changes)
  const { data: event = null, isLoading, isError } = useQuery({
    queryKey: ['event', id],
    queryFn: () => getEventById(id as string),
    enabled: !!id,
    refetchOnMount: 'always',
  });
  const setEvent = (updated: Event) => queryClient.setQueryData(['event', id], updated);
  const statusTransition = useEventStatusTransition(event, setEvent);

  // Active tab lives in the URL (?tab=) so refresh / back keep it
  // Setup first (Información, Precios, Programa), then operation (Participantes, Pagos, Reportes)
  const TABS: EventDetailTab[] = ['overview', 'pricing', 'sessions', 'participants', 'payments', 'reports'];
  const tabParam = searchParams.get('tab') as EventDetailTab | null;
  const activeTab: EventDetailTab = tabParam && TABS.includes(tabParam) ? tabParam : 'overview';
  const setActiveTab = (tab: EventDetailTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Counters on the tabs: where work is waiting
  const { data: stats } = useEventParticipantStatistics(id ?? '');
  const { data: payments = [] } = useQuery({
    queryKey: ['event_payments', id],
    queryFn: () => getEventPayments(id as string),
    enabled: !!id && can('event_payment.view'),
  });
  const pendingReview = stats?.pre_registered ?? 0;
  const paymentsWithoutReceipt = payments.filter((p) => p.status === 'RECORDED' && !p.receipt_id).length;

  // Session dialog state
  const [isSessionFormOpen, setIsSessionFormOpen] = useState(false);
  const [editingSession, setEditingSession] = useState<EventSession | null>(null);
  const [deletingSession, setDeletingSession] = useState<EventSession | null>(null);

  // CRUD hooks
  const { deleteEvent, isDeleting } = useEvent();
  const { data: sessions = [], isLoading: isLoadingSessions } = useEventSessions(id ?? '');
  const { deleteSession, isDeleting: isDeletingSession } = useSessionMutations(id ?? '');

  useEffect(() => {
    if (!id) navigate('/events');
  }, [id, navigate]);

  const handleEdit = () => navigate(`/events/${id}/edit`);

  const handleDelete = async () => {
    if (!id) return;
    const success = await deleteEvent(id);
    if (success) navigate('/events');
  };

  const handleAddSession = () => {
    setEditingSession(null);
    setIsSessionFormOpen(true);
  };

  const handleEditSession = (session: EventSession) => {
    setEditingSession(session);
    setIsSessionFormOpen(true);
  };

  const handleDeleteSession = (session: EventSession) => {
    setDeletingSession(session);
  };

  const handleConfirmDeleteSession = async () => {
    if (!deletingSession) return;
    await deleteSession(deletingSession.id);
    setDeletingSession(null);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto flex justify-center py-20">
        <InlineLoader message="Cargando evento..." />
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="container mx-auto flex flex-col items-center gap-4 py-20 text-center">
        <p className="text-muted-foreground">Evento no encontrado o sin permiso para verlo.</p>
        <Button variant="outline" onClick={() => navigate('/events')}>
          <ArrowLeft className="mr-2 size-4" /> Volver a Eventos
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/events')} className="shrink-0">
            <ArrowLeft className="mr-2 size-4" />
            Eventos
          </Button>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-bold">{event.name}</h1>
              <EventStatusControls
                event={event}
                canEdit={canTransition}
                onRequest={statusTransition.request}
                disabled={statusTransition.isSubmitting}
              />
            </div>
            <p className="flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
              <span>{event.code}</span>
              <span className="flex items-center gap-1">
                <CalendarDays className="size-3.5" /> {formatDateRange(event)}
              </span>
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {canEdit && (
            <Button variant="outline" size="sm" onClick={handleEdit}>
              <Edit className="mr-2 size-4" />
              Editar
            </Button>
          )}
          {canDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Más acciones">
                  <MoreVertical className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="mr-2 size-4" />
                  Eliminar evento
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {event.status === 'COMPLETED' && (
        <Alert className="mb-6">
          <AlertDescription>Evento completado: solo lectura. No se pueden registrar inscripciones, pagos ni asistencia.</AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as EventDetailTab)}
      >
        <TabsList className="flex w-full justify-start overflow-x-auto sm:grid sm:grid-cols-6">
          <TabsTrigger value="overview">Información</TabsTrigger>
          <TabsTrigger value="pricing">Precios</TabsTrigger>
          <TabsTrigger value="sessions">Programa</TabsTrigger>
          <TabsTrigger value="participants">
            Participantes
            {pendingReview > 0 && (
              <Badge className="ml-2 bg-amber-100 px-1.5 text-amber-800 hover:bg-amber-100" title={`${pendingReview} por revisar`}>
                {pendingReview}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="payments">
            Pagos
            {paymentsWithoutReceipt > 0 && (
              <Badge className="ml-2 bg-amber-100 px-1.5 text-amber-800 hover:bg-amber-100" title={`${paymentsWithoutReceipt} pagos sin recibo`}>
                {paymentsWithoutReceipt}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports">Reportes</TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <TabsContent value="overview">
            <EventOverviewTab
              event={event}
              canTransition={canTransition}
              onRequestStatus={statusTransition.request}
            />
          </TabsContent>

          <TabsContent value="sessions" className="space-y-6">
            <EventSessionsTab
              sessions={sessions}
              isLoading={isLoadingSessions}
              canEdit={canEdit}
              onAddSession={handleAddSession}
              onEditSession={handleEditSession}
              onDeleteSession={handleDeleteSession}
            />
          </TabsContent>

          <TabsContent value="participants">
            <EventParticipantsTab 
              eventId={event.id} 
              basePrice={event.base_price || 0} 
              eventName={event.name}
              acceptsRegistrations={acceptsRegistrations(event)}
              eventStatus={event.status}
              event={event}
              canTransition={canTransition}
              onRequestStatus={statusTransition.request}
            />
          </TabsContent>

          <TabsContent value="pricing">
            <PricingRulesTab eventId={event.id} basePrice={event.base_price || 0} />
          </TabsContent>

          <TabsContent value="payments">
            <EventPaymentsTab eventId={event.id} eventName={event.name} />
          </TabsContent>

          <TabsContent value="reports">
            <EventReportsTab eventId={event.id} eventName={event.name} />
          </TabsContent>
        </div>
      </Tabs>

      {statusTransition.dialog}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar evento?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás a punto de eliminar el evento <strong>{event.name}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Session Form Dialog */}
      {id && (
        <SessionFormDialog
          open={isSessionFormOpen}
          onOpenChange={setIsSessionFormOpen}
          eventId={id}
          session={editingSession}
          eventStartDate={event?.start_date}
          eventEndDate={event?.end_date}
        />
      )}

      {/* Delete Session Confirmation */}
      <AlertDialog open={!!deletingSession} onOpenChange={() => setDeletingSession(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar sesión?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás a punto de eliminar la sesión <strong>{deletingSession?.name}</strong>.
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingSession}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDeleteSession}
              disabled={isDeletingSession}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isDeletingSession ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
